/**
 * Enter submits; Shift+Enter breaks the line.
 *
 * Comments are messages, and in every messaging tool anybody uses, Enter
 * sends. A comment box where Enter opens a new paragraph and the only way to
 * post is to reach for a button is the single most complained-about thing
 * about rich text in a discussion thread.
 *
 * Kept as a pure function so the rule is testable without an editor — the
 * conditions below are the whole feature, and getting one wrong means either
 * a comment posted mid-sentence or a comment that cannot be posted at all.
 */

export interface KeyContext {
  key: string;
  shiftKey: boolean;
  /** True while the `@` popover is open and owns the keyboard. */
  mentionOpen: boolean;
  /** True when the editor holds nothing worth sending. */
  empty: boolean;
  /** True inside a list or code block, where Enter has structural meaning. */
  inBlock: boolean;
}

export type KeyOutcome = 'submit' | 'newline' | 'pass';

/**
 * What Enter should do right now.
 *
 * The order of these checks is the design:
 *
 *   1. The mention popover wins. It is showing a list and Enter picks from it;
 *      submitting instead would post a half-typed `@qu` and lose the choice.
 *   2. Shift+Enter is always a line break. That is the escape hatch, and it
 *      has to work everywhere or the rule above becomes a trap.
 *   3. Inside a list or a code block, Enter keeps its structural meaning —
 *      somebody writing a three-point comment expects a second bullet, not a
 *      posted comment after the first.
 *   4. An empty box does nothing. Enter on nothing posting an empty comment is
 *      how threads fill with blank rows.
 *   5. Otherwise: send.
 */
export function enterOutcome(context: KeyContext): KeyOutcome {
  if (context.key !== 'Enter') {
    return 'pass';
  }
  if (context.mentionOpen) {
    return 'pass';
  }
  if (context.shiftKey) {
    return 'newline';
  }
  if (context.inBlock) {
    return 'pass';
  }
  if (context.empty) {
    return 'pass';
  }
  return 'submit';
}

/** The controls a comment box offers. Deliberately short. */
export interface CommentTool {
  id: 'bold' | 'italic' | 'code' | 'bulletList' | 'link';
  label: string;
  glyph: string;
}

/**
 * Six controls, and no more.
 *
 * No headings, no fonts, no line spacing: a comment is a message, and a
 * message with an H1 in it is somebody using the wrong tool. The notes editor
 * is where those belong.
 */
export const COMMENT_TOOLS: CommentTool[] = [
  { id: 'bold', label: 'Bold', glyph: 'B' },
  { id: 'italic', label: 'Italic', glyph: 'I' },
  { id: 'code', label: 'Code', glyph: '<>' },
  { id: 'bulletList', label: 'Bullet list', glyph: '•' },
  { id: 'link', label: 'Link', glyph: '🔗' },
];

/**
 * Whether a typed link is safe to attach.
 *
 * The value goes straight into an href. `javascript:` there is script
 * execution one click away, inside something that looks like an ordinary link
 * in a thread.
 */
export function isSafeLinkHref(href: string): boolean {
  const trimmed = href.trim();
  if (trimmed === '') {
    return false;
  }
  return /^(https?:\/\/|mailto:|\/)/i.test(trimmed);
}

/** A bare domain typed into the link box becomes https, not a relative path. */
export function normalizeLinkHref(href: string): string {
  const trimmed = href.trim();
  if (trimmed === '') {
    return '';
  }
  if (/^(https?:\/\/|mailto:|\/)/i.test(trimmed)) {
    return trimmed;
  }
  // "example.com/docs" is a website, not a path on this app. Without this it
  // would resolve against the hub's own origin and 404.
  return `https://${trimmed}`;
}
